<template>
    <div class="report-container">

        <main class="content-main">
            <section class="title-group">
                <h1 class="main-title-text">这一路</h1>
                <div class="sub-title-wrapper">
                    <span class="sparkle-animation">✨</span>
                    <h2 class="sub-title-text">见证你的闪光轨迹</h2>
                </div>
            </section>

            <section class="timeline-container">
                <div class="timeline-axis"></div>

                <div class="timeline-node node-left fade-in-up" v-if="projectName">
                    <div class="node-dot dot-teal"></div>
                    <div class="node-card">
                        <span class="node-tag tag-teal">重要项目</span>
                        <p class="node-text">
                            参与了 <span class="text-highlight-teal">「{{ projectName }}」</span>，在团队中留下了不可磨灭的深刻记忆。
                        </p>
                    </div>
                </div>

                <div class="timeline-node node-right fade-in-up delay-1" v-if="isTeacherUnlocked">
                    <div class="node-dot dot-gold"></div>
                    <div class="node-card">
                        <span class="node-tag tag-gold">荣誉认证</span>
                        <p class="node-text">
                            成功解锁了 <span class="text-highlight-gold">“{{ certificateName }}”</span> 身份，从此开始传递属于你的微光。
                        </p>
                    </div>
                </div>

                <div class="timeline-node node-left fade-in-up delay-2" v-if="nextLevel">
                    <div class="node-dot dot-orange"></div>
                    <div class="node-card">
                        <span class="node-tag tag-orange">荣耀晋升</span>
                        <p class="node-text">
                            晋升至 <span class="text-highlight-orange">{{ nextLevel }}</span>！迈向更广阔的舞台，等待下一次华丽绽放。
                        </p>
                    </div>
                </div>
            </section>
        </main>

        <div class="bottom-glow"></div>
    </div>
</template>

<script>
export default {
    name: 'ReportTimelinePage',
    props: ["ReportData"],

    computed: {
        // 1. 解析 activity2 字段（提取：重要项目、晋升）
        parsedActivity2() {
            const result = { projectName: '', nextLevel: '' };
            if (!this.ReportData || !this.ReportData.activity2) return result;

            try {
                const list = JSON.parse(this.ReportData.activity2);
                if (Array.isArray(list)) {
                    list.forEach(item => {
                        // 动态匹配包含“项目”字样的键
                        const projectKey = Object.keys(item).find(key => key.includes('项目'));
                        if (projectKey) result.projectName = item[projectKey];
                        // 匹配晋升
                        if (item['晋升']) result.nextLevel = item['晋升'];
                    });
                }
            } catch (e) {
                console.error("解析 activity2 失败:", e);
            }
            return result;
        },

        // 2. 解析 activity 字段，提取真实的证书名称
        certificateName() {
            if (!this.ReportData || !this.ReportData.activity) return '';

            try {
                const list = JSON.parse(this.ReportData.activity);
                if (Array.isArray(list) && list.length > 0) {
                    // 寻找第一个含有 certificate 字段的值
                    const target = list.find(item => item.certificate);
                    return target ? target.certificate : '';
                }
            } catch (e) {
                console.error("解析 activity 证书名称失败:", e);
            }
            return '';
        },

        // 3. 动态判定荣誉节点是否显示（只有成功拿到真实的证书名称时才显示，否则直接隐藏）
        isTeacherUnlocked() {
            return !!this.certificateName; // 转为布尔值，字符串为空时返回 false
        },

        // 4. 映射项目与晋升变量（若为空，则返回空字符串，触发 v-if 隐藏）
        projectName() {
            return this.parsedActivity2.projectName;
        },
        nextLevel() {
            return this.parsedActivity2.nextLevel;
        }
    }
}
</script>

<style scoped>
/* 容器及原汁原味背景设定 */
.report-container {
    position: relative;
    width: 100%;
    height: 100vh;
    overflow: hidden;
    box-sizing: border-box;
    padding: 35px 24px;
    background:
        url("../../assets/26June/logo.png") right top no-repeat,
        url("../../assets/26June/bg.jpg") no-repeat;
    background-size: 100% 100%, cover;
    font-family: -apple-system, "SF Pro Text", "PingFang SC", sans-serif;
    display: flex;
    flex-direction: column;
}

/* 顶部 Logo */
.logo-text {
    display: flex;
    flex-direction: column;
}

.main-title {
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 1px;
    color: #0f172a;
    opacity: 0.8;
}

.sub-title {
    font-size: 8px;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    opacity: 0.7;
}

/* 主体容器 */
.content-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    padding-top: 30px;
    z-index: 2;
}

/* 1. 大标题排版 */
.title-group {
    margin-bottom: 40px;
}

.main-title-text {
    font-size: 34px;
    font-weight: 900;
    margin: 0;
    letter-spacing: 4px;
    color: #004d40;
}

.sub-title-wrapper {
    display: flex;
    align-items: center;
    margin-top: 6px;
}

.sparkle-animation {
    font-size: 18px;
    margin-right: 8px;
    animation: pulse 1.5s infinite ease-in-out;
}

.sub-title-text {
    font-size: 20px;
    font-weight: 600;
    margin: 0;
    color: #1e293b;
    letter-spacing: 1px;
}

/* 2. 精致星轨时光轴核心结构 */
.timeline-container {
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 30px;
    padding-left: 20px;
    /* 整体往右挪，留出左边画时间轴 */
    box-sizing: border-box;
}

/* 纵向发光轴线 */
.timeline-axis {
    position: absolute;
    left: 6px;
    top: 5px;
    bottom: 15px;
    width: 2px;
    /* 由深向浅的微发光渐变线 */
    background: linear-gradient(to bottom, rgba(0, 77, 64, 0.5), rgba(230, 81, 0, 0.4), rgba(255, 255, 255, 0.1));
    box-shadow: 0 0 8px rgba(0, 77, 64, 0.2);
}

/* 时光轴节点基础样式 */
.timeline-node {
    position: relative;
    width: 100%;
    padding-left: 20px;
    box-sizing: border-box;
}

/* 轴线上的发光晶体圆点 */
.node-dot {
    position: absolute;
    left: -19px;
    /* 精准定位到轴线上 */
    top: 6px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #fff;
    border: 2px solid #004d40;
    z-index: 5;
}

.dot-teal {
    border-color: #00796b;
    box-shadow: 0 0 8px #00796b;
}

.dot-gold {
    border-color: #ff8f00;
    box-shadow: 0 0 8px #ff8f00;
}

.dot-orange {
    border-color: #e65100;
    box-shadow: 0 0 8px #e65100;
}

/* 自由开放式文字区（不要硬框，改用底部一条轻量呼吸阴影） */
.node-card {
    background: transparent;
}

/* 精致的小标签 */
.node-tag {
    display: inline-block;
    font-size: 14px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 4px;
    letter-spacing: 0.5px;
    margin-bottom: 8px;
}

.tag-teal {
    background: rgba(0, 121, 107, 0.12);
    color: #004d40;
    border: 1px solid rgba(0, 121, 107, 0.2);
}

.tag-gold {
    background: rgba(255, 143, 0, 0.12);
    color: #b66100;
    border: 1px solid rgba(255, 143, 0, 0.2);
}

.tag-orange {
    background: rgba(230, 81, 0, 0.12);
    color: #d84315;
    border: 1px solid rgba(230, 81, 0, 0.2);
}

/* 叙事段落文本（深蓝黑，在蓝色背景上字字清晰） */
.node-text {
    font-size: 18px;
    line-height: 1.8;
    color: #1e293b;
    margin: 0;
    letter-spacing: 0.4px;
}

/* 亮点文本色彩微调 */
.text-highlight-teal {
    color: #00796b;
    font-weight: 700;
    font-size: 18px;
}

.text-highlight-gold {
    color: #e65100;
    font-weight: 700;
    font-size: 18px;
}

.text-highlight-orange {
    background: linear-gradient(135deg, #d84315, #ff6f00);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 800;
    font-size: 18px;
}

/* 底部极简修饰 */
.bottom-glow {
    position: absolute;
    bottom: 0;
    right: 0;
    left: 0;
    height: 60px;
    background: linear-gradient(to top, rgba(255, 255, 255, 0.1), transparent);
    pointer-events: none;
}

/* --- 时光轴专属入场动效 --- */
.fade-in-up {
    animation: fadeInUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.delay-1 {
    animation-delay: 0.2s;
}

.delay-2 {
    animation-delay: 0.4s;
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes pulse {

    0%,
    100% {
        transform: scale(1);
        opacity: 1;
    }

    50% {
        transform: scale(1.2);
        opacity: 0.7;
    }
}

/* 小屏自适应 */
@media screen and (max-height: 670px) {
    .title-group {
        margin-bottom: 25px;
    }

    .main-title-text {
        font-size: 28px;
    }

    .timeline-container {
        gap: 20px;
    }

    .node-text {
        font-size: 16px;
    }
}

/* 桌面隔离沙盒 */
@media screen and (min-width: 1024px) {
    .report-container {
       width: 100% !important;
        height: 100vh !important;
        padding: 60px 10%; /* 侧边增加安全留白，防止内容贴边 */
        
        /* 单一背景图完美全屏铺满，不留任何死角 */
        background-image: url("../../assets/26June/pcbg.png") !important;
        background-position: center center !important;
        background-repeat: no-repeat !important;
        background-size: cover !important; /* 核心：等比例拉伸，宁可裁剪也绝不留白 */
        
        /* 彻底移除手机端的圆角和边界阴影 */
        border-radius: 0 !important;
        box-shadow: none !important;
        border: none !important;
    }
    .text-highlight-orange, .text-highlight-teal, .text-highlight-gold {
        font-weight: 800;
        font-size: 28px;
    }

}
</style>